import React, { useEffect, useMemo } from "react";
import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { closeCreateSubjectAssistPhone } from "../../../../modules/admin-modules/models/Actions";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminPillSelectStyles,
} from "../../../../components/Models/admin-modal-theme";
import { useForm } from "react-hook-form";
import MainInput from "../../../../components/Form/input";
import {
  createSubjectAssistPhoneRequest,
  editSubjectAssistPhoneRequest,
} from "../../../../modules/admin-modules/subject-assist-phones/Actions";
import Select from "react-select";

const AddSubjectAssistPhone = ({ isAdd, data, setSelectedData }) => {
  const openPhone = useSelector((state) => state.model.isOpen40);
  const isLoading = useSelector((state) => state.subjectAssistPhones.isLoading);
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: data ? data : { type: 1 },
    shouldUnregister: true,
  });

  const typeValue = watch("type");
  const selectStyles = useMemo(() => {
    const base = adminPillSelectStyles({ menuZIndex: 50 });
    return {
      ...base,
      menuPortal: (provided) => ({ ...provided, zIndex: 2000 }),
    };
  }, []);

  useEffect(() => {
    register("type", { required: "Type is required" });
    if (!data || isAdd) {
      setValue("type", 1);
    }
  }, [register, setValue, data, isAdd]);

  useEffect(() => {
    if (data && !isAdd) {
      reset({
        phone: data.phone || "",
        type: data.type || 1,
      });
      setValue("type", data.type || 1);
    } else {
      reset({
        phone: "",
        type: 1,
      });
      setValue("type", 1);
    }
  }, [data, isAdd, reset, setValue]);

  const typeOptions = [
    { value: 1, label: "Secretarial" },
    { value: 2, label: "Academic" },
  ];

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color={brandPalette.primary}
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        {isAdd ? "Add Assistant Phone Line" : "Edit Assistant Phone Line"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Phone number and line type
      </Text>
    </Box>
  );

  const onSubmit = async (formData) => {
    if (!formData.phone) {
      return;
    }
    if (!formData.type) {
      formData.type = 1;
    }

    const action = () => {
      dispatch(closeCreateSubjectAssistPhone());
      setSelectedData({});
      reset();
    };

    if (isAdd) {
      await dispatch(createSubjectAssistPhoneRequest(formData, action));
    } else {
      await dispatch(
        editSubjectAssistPhoneRequest({
          id: data?.id,
          formData: formData,
          action: action,
        })
      );
    }
  };

  const closeFunc = () => {
    dispatch(closeCreateSubjectAssistPhone());
    setSelectedData({});
    reset();
  };

  return (
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openPhone}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="2xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} dir="ltr" w="100%">
          <MainInput
            label="Phone"
            error={errors?.phone?.message}
            placeholder="+00 000 000 0000"
            register={register}
            name="phone"
            required
          />

          <FormControl>
            <VStack align="stretch" spacing={2}>
              <FormLabel
                m={0}
                fontSize="14px"
                fontWeight="700"
                color={brandPalette.primary}
                textAlign="left"
              >
                Line type
              </FormLabel>
              <Select
                options={typeOptions}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                menuPosition="fixed"
                onChange={(selected) => {
                  setValue("type", selected?.value ?? 1, {
                    shouldValidate: true,
                  });
                }}
                value={
                  typeOptions.find(
                    (option) => option.value === (typeValue || 1)
                  ) || typeOptions[0]
                }
                styles={selectStyles}
                placeholder="Select type"
              />
            </VStack>
            {errors?.type && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {errors.type.message}
              </Text>
            )}
          </FormControl>

          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save Changes
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>
  );
};

export default AddSubjectAssistPhone;
